// ─── S1 XP System ───
// XP curve: quadratic scaling
// Level 1→2: 100 XP, Level 10→11: 550 XP, Level 50→51: 2,550 XP
export function xpToNextLevel(level) {
  return 100 + (level - 1) * 50;
}

// Total cumulative XP required to reach a given level (from level 1)
export function totalXpForLevel(level) {
  if (level <= 1) return 0;
  const n = level - 1;
  return n * 100 + 50 * n * (n - 1) / 2;
}

// Given total XP, return { level, currentXp, xpToNext }
export function levelFromTotalXp(totalXp) {
  let level = 1;
  let xpUsed = 0;
  const MAX_LEVEL = 1000;
  while (level < MAX_LEVEL) {
    const needed = xpToNextLevel(level);
    if (xpUsed + needed > totalXp) {
      return {
        level,
        currentXp: totalXp - xpUsed,
        xpToNext: needed,
      };
    }
    xpUsed += needed;
    level++;
  }
  return { level: MAX_LEVEL, currentXp: totalXp - xpUsed, xpToNext: 0 };
}

// S1 XP awards
export const XP_AWARDS = {
  PARTICIPATION: 50,   // Just for playing
  WIN_BONUS: 75,       // Additional XP for winning
  PER_TASK: 10,        // Per task completed in-game
};

// S1 Pass tier XP formula
export function xpForPassTier(tier) {
  return 200 + tier * 15;
}

export function totalXpForPassTier(tier) {
  if (tier <= 0) return 0;
  return tier * 200 + 15 * tier * (tier + 1) / 2;
}

export function passTierFromXp(roleXp) {
  let tier = 0;
  let xpUsed = 0;
  while (tier < 100) {
    const needed = xpForPassTier(tier + 1);
    if (xpUsed + needed > roleXp) {
      return {
        tier,
        currentXp: roleXp - xpUsed,
        xpToNext: needed,
      };
    }
    xpUsed += needed;
    tier++;
  }
  return { tier: 100, currentXp: 0, xpToNext: 0 };
}

// ─── S2 XP System ───
// S2 XP awards per game
export const S2_XP_AWARDS = {
  PARTICIPATION: 20,   // Playing a game
  CREWMATE_WIN: 20,    // Win bonus as crewmate
  IMPOSTOR_WIN: 100,   // Win bonus as impostor
  PER_KILL: 5,         // Per kill
  PER_TASK: 2,         // Per task completed
  // Note: challenge XP rewards are defined per-challenge in challengePool.js (100-750 XP each)
};

// S2 tier XP requirements (50,000 total for 100 tiers)
// Tiers 1-25: 150 XP each (3,750)
// Tiers 26-50: 350 XP each (8,750)
// Tiers 51-75: 600 XP each (15,000)
// Tiers 76-100: 900 XP each (22,500)
export function s2XpForTier(tier) {
  if (tier <= 25) return 150;
  if (tier <= 50) return 350;
  if (tier <= 75) return 600;
  return 900;
}

// Total XP needed to reach a given tier (1-indexed)
export function s2TotalXpForTier(tier) {
  if (tier <= 0) return 0;
  let total = 0;
  for (let t = 1; t <= tier; t++) {
    total += s2XpForTier(t);
  }
  return total;
}

// Post-100: 1200 XP per additional level
export const S2_POST_100_XP = 1200;

// Given total XP, return current tier and progress for S2
export function s2TierFromXp(totalXp) {
  let tier = 0;
  let xpUsed = 0;

  // Tiers 1-100
  while (tier < 100) {
    const needed = s2XpForTier(tier + 1);
    if (xpUsed + needed > totalXp) {
      return {
        tier,
        currentXp: totalXp - xpUsed,
        xpToNext: needed,
        isPost100: false,
      };
    }
    xpUsed += needed;
    tier++;
  }

  // Post-100 levels
  const remainingXp = totalXp - xpUsed;
  const extraLevels = Math.floor(remainingXp / S2_POST_100_XP);
  const currentXpInLevel = remainingXp % S2_POST_100_XP;

  return {
    tier: 100 + extraLevels,
    currentXp: currentXpInLevel,
    xpToNext: S2_POST_100_XP,
    isPost100: true,
  };
}
